import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { formatWeekdayDateOrDash } from "@shared-core/domain/formatters";
import type { ProofView } from "@shared-core/data/customer-link-adapter";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { AlertTriangle, CheckCircle2, FileText, XCircle } from "lucide-react";
import { ArtworkCheckIssueGroups } from "./ArtworkCheckIssueGroups";
import { useFetchedFile, type FetchFile } from "./useFetchedFile";

// The parts of reviewing one proof that the order page (in place, `?proof=`)
// and the per-proof page (`/proof/:token`) share: the PDF, the files and
// check results it was made from, and the outcome once it is decided. Every
// sentence is the read's; these only place it.

export interface ProofPdfSource {
  /** Fetches a read's API-relative URL (the PDF's) with the session and the chosen customer. */
  fetchFile: FetchFile;
}

/** The proof's PDF in a frame, with a button to open it in its own tab; or, when it will not load, one quiet sentence. */
export function ProofPdf({
  proof,
  title,
  source,
}: {
  proof: ProofView;
  title: string;
  source: ProofPdfSource;
}) {
  const capabilities = usePlatformCapabilities();
  // A page that names no PDF route (the staff view-as mirror) fetches nothing.
  const { state, retry } = useFetchedFile(source.fetchFile, proof.pdf_url);

  // The proof's files and checks still show around it.
  if (proof.pdf_url === null) return null;
  if (state.status === "loading") {
    return (
      <Card
        role="status"
        className="aspect-[4/3] max-h-[70vh] w-full items-center justify-center text-sm text-muted-foreground"
      >
        Loading {proof.label}…
      </Card>
    );
  }
  if (state.status === "failed") {
    return (
      <Card className="items-center gap-2 p-6 text-center">
        <AlertTriangle className="size-6 text-muted-foreground" />
        <p className="font-medium">
          We can&apos;t show this proof here just now.
        </p>
        <p className="text-sm text-muted-foreground">
          Try again in a few minutes, or get in touch and we&apos;ll send it to
          you.
        </p>
        <Button variant="outline" size="sm" onClick={retry}>
          Try again
        </Button>
      </Card>
    );
  }
  return (
    // An iframe, not an <object>: the portal's CSP has object-src 'none' and frame-src 'self' blob:.
    <div className="space-y-1">
      <iframe
        src={state.objectUrl}
        title={title}
        className="aspect-[4/3] max-h-[70vh] w-full rounded-lg border bg-muted/40"
      />
      <button
        type="button"
        onClick={() => capabilities.openBlobInNewTab(state.file.blob)}
        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <FileText className="size-4" />
        Open {proof.label} (PDF)
      </button>
    </div>
  );
}

/** The files a proof was made from and the check's findings on them. */
export function ProofMadeFrom({ proof }: { proof: ProofView }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Made from</p>
      <ul className="space-y-1 text-sm">
        {proof.files.map((f) => (
          <li
            key={`${f.part}-${f.file_name}`}
            className="flex flex-wrap items-center gap-2"
          >
            <FileText className="size-4 text-muted-foreground" />
            {f.part && <span className="text-muted-foreground">{f.part}:</span>}
            <span>{f.file_name}</span>
            <span className="text-xs text-muted-foreground">
              {f.reused_from_revision
                ? "same file as before"
                : `${formatWeekdayDateOrDash(f.uploaded_at)} · ${f.uploaded_by}`}
            </span>
            {f.split_from && (
              <span className="text-xs text-muted-foreground">
                From {f.split_from.file_name}, {f.split_from.pages_label}
              </span>
            )}
          </li>
        ))}
      </ul>
      {proof.findings.length > 0 && (
        <div className="space-y-2 pt-1">
          <ArtworkCheckIssueGroups findings={proof.findings} />
        </div>
      )}
    </div>
  );
}

/** What a proof that no longer awaits a decision says: approved, changes asked for, or replaced. */
export function ProofOutcome({ proof }: { proof: ProofView }) {
  if (proof.state === "approved") {
    return (
      <Card className="gap-2 p-4">
        <p className="flex items-center gap-2 font-medium">
          <CheckCircle2 className="size-5 text-green-700 dark:text-green-400" />
          {proof.status_line}
        </p>
        {proof.accepted_findings.length > 0 && (
          <div className="pl-7 text-sm text-muted-foreground">
            <p>Approved knowing:</p>
            <ul className="list-disc pl-5">
              {proof.accepted_findings.map((f) => (
                <li key={f.id}>{f.sentence}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    );
  }
  if (proof.state === "rejected") {
    return (
      <Card className="gap-2 p-4">
        <p className="flex items-center gap-2 font-medium">
          <XCircle className="size-5 text-muted-foreground" />
          {proof.status_line}
        </p>
        {proof.comment && (
          <p className="pl-7 text-sm italic text-muted-foreground">
            {proof.reason_label ? `${proof.reason_label}: ` : ""}
            &ldquo;{proof.comment}&rdquo;
          </p>
        )}
        <p className="pl-7 text-sm text-muted-foreground">
          {proof.is_answered
            ? "We've sent a newer proof since."
            : "Your comments are with our studio. We'll send you a new proof."}
        </p>
      </Card>
    );
  }
  if (proof.state === "superseded") {
    return (
      <Card className="gap-1 p-4">
        <p className="font-medium">This proof was replaced</p>
        <p className="text-sm text-muted-foreground">
          A new file arrived after we sent it, so we sent a new proof.
        </p>
      </Card>
    );
  }
  // Out but not open to a decision from here: its status line says where it stands.
  return (
    <Card className="p-4">
      <p className="text-sm text-muted-foreground">{proof.status_line}</p>
    </Card>
  );
}
